<template>
  <v-card class="pa-4" flat>
    <!-- جدول محصولات آرشیو -->
    <vue-table
      :data="products"
      :headers="tableColumns"
      :loading.sync="loading"
      @change="onPagination"
    >
      <!-- فیلترها -->
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              class="rounded-lg"
              dense
              hide-details
              label="تعداد در صفحه"
              outlined
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="4" md="3">
            <v-text-field
              v-model="props.search"
              label="جستجو (عنوان یا SKU)"
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
              class="rounded-lg"
              @keyup.enter="() => onPagination(props)"
              @click:clear="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.has_variants"
              :items="variantOptions"
              label="نوع محصول"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.is_active"
              :items="statusOptions"
              label="وضعیت فعالیت"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.in_stock"
              :items="stockOptions"
              label="وضعیت موجودی"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>
        </v-row>
      </template>

      <!-- ردیف -->
      <template v-slot:index="{ item }">
        {{ persian(item.index + 1 + (products?.current_page - 1) * products?.per_page) }}
      </template>

      <!-- عنوان -->
      <template v-slot:title="{ item }">
        <div class="font-weight-bold">{{ item.title }}</div>
        <small class="grey--text">کد کالا: {{ item.sku }}</small>
      </template>

      <!-- نوع کالا -->
      <template v-slot:has_variants="{ item }">
        <v-chip
          small
          :color="item.has_variants ? 'deep-purple lighten-5' : 'blue lighten-5'"
          :text-color="item.has_variants ? 'deep-purple darken-3' : 'blue darken-3'"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ item.has_variants ? 'mdi-sitemap' : 'mdi-cube-outline' }}</v-icon>
          {{ item.has_variants ? 'دارای تنوع' : 'محصول ساده' }}
        </v-chip>
      </template>

      <!-- موجودی -->
      <template v-slot:stock="{ item }">
        <v-chip
          small
          :color="item.stock > 0 ? 'green lighten-5' : 'red lighten-5'"
          :text-color="item.stock > 0 ? 'green darken-3' : 'red darken-3'"
          class="font-weight-bold"
        >
          {{ persian(item.stock || 0) }} عدد
        </v-chip>
      </template>

      <!-- آمار تنوع و لیستینگ -->
      <template v-slot:stats="{ item }">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" small outlined color="primary" class="ml-1">
                <v-icon left x-small>mdi-tag-multiple</v-icon>
                {{ persian(item.skus_count || 0) }}
              </v-chip>
            </template>
            <span>تعداد تنوع‌ها (SKU)</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" small outlined color="teal">
                <v-icon left x-small>mdi-store-sync</v-icon>
                {{ persian(item.listings_count || 0) }}
              </v-chip>
            </template>
            <span>تعداد لیستینگ‌های متصل</span>
          </v-tooltip>
        </div>
      </template>

      <!-- وضعیت -->
      <template v-slot:is_active="{ item }">
        <v-chip
          small
          :color="item.is_active ? 'teal lighten-5' : 'grey lighten-3'"
          :text-color="item.is_active ? 'teal darken-3' : 'grey darken-2'"
        >
          {{ item.is_active ? 'فعال' : 'غیرفعال' }}
        </v-chip>
      </template>
    </vue-table>
  </v-card>
</template>

<script>
export default {
  name: "ArchivedProductsListTab",
  data() {
    return {
      loading: false,
      products: [],
      variantOptions: [
        { text: "همه", value: null },
        { text: "دارای تنوع", value: 1 },
        { text: "ساده", value: 0 },
      ],
      statusOptions: [
        { text: "همه", value: null },
        { text: "فعال", value: 1 },
        { text: "غیرفعال", value: 0 },
      ],
      stockOptions: [
        { text: "همه", value: null },
        { text: "موجود", value: 1 },
        { text: "ناموجود", value: 0 },
      ],
      tableProps: {
        page: 1,
        length: 10,
        search: null,
        has_variants: null,
        is_active: null,
        in_stock: null,
      },
    };
  },

  computed: {
    tableColumns() {
      return [
        { value: "index", text: "ردیف", align: "center", sortable: false },
        { value: "title", text: "عنوان و SKU", align: "start", sortable: true },
        { value: "has_variants", text: "نوع", align: "center", sortable: false },
        { value: "stock", text: "موجودی کل", align: "center", sortable: true },
        { value: "stats", text: "تنوع / اتصال", align: "center", sortable: false },
        { value: "is_active", text: "وضعیت", align: "center", sortable: false },
      ];
    },
  },

  mounted() {
    this.fetchArchiveProducts();
  },

  methods: {
    async fetchArchiveProducts() {
      this.loading = true;
      const params = {};
      Object.keys(this.tableProps).forEach((key) => {
        if (this.tableProps[key] !== null && this.tableProps[key] !== "") {
          params[key] = this.tableProps[key];
        }
      });

      try {
        const { data } = await this.$api.get("/v1/products?trashed=true", { params });
        this.products = data.data || data;
      } catch (e) {
        this.$toast.error("خطا در دریافت لیست محصولات آرشیو");
      } finally {
        this.loading = false;
      }
    },

    onPagination(e) {
      if (e && typeof e === "object" && !Array.isArray(e)) {
        this.tableProps = { ...this.tableProps, ...e };
        if ("search" in e || "has_variants" in e || "is_active" in e || "in_stock" in e) {
          this.tableProps.page = 1;
        }
      }
      this.fetchArchiveProducts();
    },
  },
};
</script>
